import DataMap from 'assets/components/dataMap/DataMap';
import Icon from 'assets/components/icon/Icon';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { formatNumberWithSeparator } from 'assets/utils/data/Number';
import { first, groupBy, keys, sumBy } from 'lodash';
import ClaimJobItemPayee from 'models/claims/claimJobItemPayee/Model.ClaimJobItemPayee';
import { ClaimCreatorOptions } from 'models/enums/ClaimCreation';
import PaymentStatus from 'models/enums/PaymentStatus';
import { useEffect } from 'react';
import { usePublicClaimSubmitProvider } from '../../../Claim.Submit.Hooks';

export default function ClaimSubmitClaimJobItems() {
  const { lang } = getLocales();
  const { form, claimTypes, claimJobForUpdate, selectedDealer, areAutomatedJobItemsEnabled, updateClaimJob } =
    usePublicClaimSubmitProvider();

  const updateValue = (fieldName: keyof Model.IClaimJobItem, value: any, templateId: number) => {
    const updatedJobItems = first(form.data?.claim?.claimJobs)?.claimJobItems?.map((item) => {
      if (item.templateId !== templateId) return item;

      const adj = fieldName === 'adjudication' ? value : item.adjudication ?? 0.0;
      const gst = fieldName === 'gstHst' ? value : item.gstHst ?? 0.0;
      const pst = fieldName === 'provincialSalesTax' ? value : item.provincialSalesTax ?? 0.0;

      return {
        ...item,
        [fieldName]: value,
        correction: adj,
        total: Number((adj + adj * (gst + pst)).toFixed(2)),
      };
    });

    updateClaimJob({ ...first(form.data?.claim?.claimJobs), claimJobItems: updatedJobItems });
  };

  useEffect(() => {
    if (
      form.data.claimCreationOption === ClaimCreatorOptions.DEALERSHIP &&
      form.data.claim?.dealerId &&
      areAutomatedJobItemsEnabled
    ) {
      const foundClaimType = claimTypes?.find((it) => it.id === claimJobForUpdate?.claimTypeId);

      const tax = foundClaimType?.products
        ?.find((it) => it.id === form.data.claim.contract.productId)
        ?.productTaxes?.find((it) => {
          return it.provinceCode === selectedDealer?.provinceCode;
        });

      const newJobItems = foundClaimType?.claimTypeJobItemTemplates?.map((it) => ({
        //#region Additional fields only for item update
        templateId: it.id,
        lossCodes: it.lossCodes,
        //#endregion
        claimJobItemPayee: new ClaimJobItemPayee({ dealer: selectedDealer, dealerId: selectedDealer?.id }),
        jobItemTypeCode: it.jobItemTypeCode,
        adjudication: it.adjudication,
        lossCode: it.lossCodes.length === 1 ? first(it.lossCodes)?.code : undefined,
        gstHst: tax?.claimsGstHst || 0.0,
        provincialSalesTax: tax?.claimsProvincialSalesTax || 0.0,
        total: it.adjudication + it.adjudication * (tax?.claimsGstHst + tax?.claimsProvincialSalesTax),
        statusCode: PaymentStatus.unpaid,
        quantity: 0,
        hoursWorked: 0,
        correction: it.adjudication,
        labourRate: selectedDealer?.labourRate,
        paymentMethodCode: selectedDealer?.paymentMethodCode,
        isCreatedAutomatically: true,
        currency: 'CAD',
      }));

      claimJobForUpdate.claimJobItems = newJobItems;

      updateClaimJob({ ...claimJobForUpdate });
    } else {
      claimJobForUpdate.claimJobItems = [];
      updateClaimJob({ ...claimJobForUpdate });
    }
  }, [claimJobForUpdate?.claimTypeId, form.data.claimCreationOption, form.data.claim?.dealerId]);

  const groupedClaimJobItems = groupBy(claimJobForUpdate?.claimJobItems, (it) => it.jobItemTypeCode);
  return !!first(form.data?.claim?.claimJobs)?.claimJobItems?.length &&
    ClaimCreatorOptions.DEALERSHIP === form.data?.claimCreationOption &&
    areAutomatedJobItemsEnabled ? (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: Theme.Size.L,
        marginLeft: Theme.Size.L,
        marginRight: Theme.Size.L,
      }}
    >
      <h2 style={{ display: 'flex', justifyContent: 'space-between' }}>{lang.jobItemTemplates}</h2>
      {keys(groupedClaimJobItems)?.map((it) => (
        <div key={it} style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ marginLeft: Theme.Size.XL, marginRight: Theme.Size.XL }}>
            <h4>{it}</h4>
          </div>
          <table className={ThemeTableSimple} style={{ fontSize: Theme.FontSize.S }}>
            <thead>
              <tr>
                <th>#</th>
                <th style={{ textAlign: 'center' }}>{lang.payee}</th>
                <th style={{ textAlign: 'center' }}>{lang.lossCode}</th>
                <th style={{ textAlign: 'center' }}>{lang.adjustedAmt}</th>
                <th style={{ textAlign: 'center' }}>GST/HST</th>
                <th style={{ textAlign: 'center' }}>PST</th>
                <th style={{ textAlign: 'center' }}>{lang.total}</th>
                <th style={{ display: 'none' }}></th>
              </tr>
            </thead>
            <tbody>
              <DataMap
                data={groupedClaimJobItems[it]}
                render={({ data: item, index }) => {
                  return (
                    <tr key={index + item.currency}>
                      <td>{index + 1}</td>
                      <td style={{ textAlign: 'center' }}>{item.claimJobItemPayee?.dealer?.name}</td>
                      <td>
                        <SelectInput
                          name="lossCode"
                          data={item.lossCodes?.map((it) => ({
                            id: it.code,
                            title: it.descnEn,
                          }))}
                          value={item?.lossCode}
                          disabled={item.lossCodes?.length === 1}
                          onChange={({ lossCode }) => updateValue('lossCode', lossCode, item.templateId)}
                          error={!item.lossCode && lang.required}
                        />
                      </td>
                      <td>
                        <NumberInput
                          name="adjudication"
                          value={Number(item?.adjudication?.toFixed(2))}
                          onChange={({ adjudication }) => updateValue('adjudication', adjudication, item.templateId)}
                          step={0.01}
                          style={{ input: { textAlign: 'right' } }}
                          icon={<Icon>{item.currency}</Icon>}
                        />
                      </td>
                      <td>
                        <NumberInput
                          name="gstHst"
                          value={Number(((item?.gstHst || 0.0) * item.adjudication).toFixed(2))}
                          onChange={({ gstHst }) => updateValue('gstHst', gstHst / item.adjudication, item.templateId)}
                          step={0.01}
                          error={
                            item?.gstHst > 1 &&
                            lang.mustBeEqualOrLessThanAmount.replace(
                              '{amount}',
                              `${lang.adjustedAmt}: ${item.adjudication.toFixed(2)}`
                            )
                          }
                          style={{ input: { textAlign: 'right' } }}
                          icon={<Icon>{item.currency}</Icon>}
                        />
                      </td>
                      <td>
                        <NumberInput
                          name="provincialSalesTax"
                          value={Number(((item?.provincialSalesTax || 0.0) * item.adjudication).toFixed(2))}
                          onChange={({ provincialSalesTax }) =>
                            updateValue('provincialSalesTax', provincialSalesTax / item.adjudication, item.templateId)
                          }
                          error={
                            item?.provincialSalesTax > 1 &&
                            lang.mustBeEqualOrLessThanAmount.replace(
                              '{amount}',
                              `${lang.adjustedAmt}: ${item.adjudication.toFixed(2)}`
                            )
                          }
                          step={0.01}
                          style={{ input: { textAlign: 'right' } }}
                          icon={<Icon>{item.currency}</Icon>}
                        />
                      </td>
                      <td style={{ padding: 0 }}>
                        <NumberInput
                          name="total"
                          value={Number(item.total.toFixed(2))}
                          readOnly
                          onChange={({ total }) => updateValue('total', total, item.templateId)}
                          style={{ input: { textAlign: 'right' } }}
                          icon={<Icon>{item.currency}</Icon>}
                        />
                      </td>
                      <td style={{ display: 'none' }}></td>
                    </tr>
                  );
                }}
              />
            </tbody>
          </table>
        </div>
      ))}
      <div>
        <table className={ThemeTableSimple} style={{ gridColumn: '-1/1', fontSize: Theme.FontSize.S }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'center', fontSize: Theme.FontSize.L }}>{lang.totalAmountWithTaxes}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ textAlign: 'center', fontSize: Theme.FontSize.L }}>
                {formatNumberWithSeparator(sumBy(claimJobForUpdate.claimJobItems, (it) => it.total).toFixed(2))} CAD
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  ) : null;
}
